/**
 * ArchiveReflection — a single italic-serif tucked note on /archive.
 *
 * One reflection visible at a time. Truthfulness is gated server-side
 * (the endpoint returns `{reflection: null}` when nothing meaningful
 * has happened) — this component renders ONLY what the backend allows.
 *
 * Visual contract (per Craig's restraint locks):
 *   • feels DISCOVERED, never broadcast
 *   • parchment card with hairline gold border at very low alpha
 *   • italic serif, sentence case, ✦ sparkle smaller than baseline
 *   • NO close button, NO badge, NO timestamp, NO count
 *   • slow entrance, slow respiration, gentle fade-out after ~14s
 *     (reflections decay naturally on display — they are not nags)
 *
 * Placement: tucked into the top-right safe area of ArchivePage, like
 * a museum placard sitting beside the shelf — never centered, never
 * obstructing the spines.
 *
 * Mode 1 surface — atmospheric editorial. Parchment + walnut family.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTRANCE_DELAY_MS = 1800;
const FADE_IN_MS = 1600;
const DWELL_MS = 14000;   // total visible time before natural decay
const FADE_OUT_MS = 2200;

export default function ArchiveReflection({ eventId }) {
  const [reflection, setReflection] = useState(null);
  const [phase, setPhase] = useState('hidden');  // hidden → entering → visible → fading

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/archive/reflection`);
        if (cancelled) return;
        const ref = r?.data?.reflection;
        if (!ref || !ref.message) return;
        setReflection(ref);
        // Slow entrance — let the shelf settle first.
        setTimeout(() => { if (!cancelled) setPhase('entering'); }, ENTRANCE_DELAY_MS);
        setTimeout(() => { if (!cancelled) setPhase('visible'); }, ENTRANCE_DELAY_MS + FADE_IN_MS);
        // Natural decay — gentle fade-out, never abrupt.
        setTimeout(() => { if (!cancelled) setPhase('fading'); }, ENTRANCE_DELAY_MS + FADE_IN_MS + DWELL_MS);
      } catch {
        // 401/403/404 → silent. The reflection surface NEVER signals
        // its own absence; a guest visiting /archive simply sees the
        // shelf, exactly as intended.
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  if (!reflection) return null;

  const opacity =
    phase === 'hidden'   ? 0 :
    phase === 'entering' ? 0 :
    phase === 'visible'  ? 1 :
    /* fading */           0;

  const transition = phase === 'fading'
    ? `opacity ${FADE_OUT_MS}ms ease-in, transform ${FADE_OUT_MS}ms ease-in`
    : `opacity ${FADE_IN_MS}ms ease-out, transform ${FADE_IN_MS}ms ease-out`;

  return (
    <>
      <style>{`
        @keyframes archiveReflectionBreathe {
          0%, 100% { box-shadow: 0 18px 38px rgba(0,0,0,0.42), 0 0 0 1px rgba(168,133,76,0.22); }
          50%      { box-shadow: 0 22px 46px rgba(0,0,0,0.48), 0 0 0 1px rgba(168,133,76,0.34); }
        }
        @media (prefers-reduced-motion: reduce) {
          .eventdrop-archive-reflection { animation: none !important; }
        }
      `}</style>
      <div
        className="eventdrop-archive-reflection"
        data-testid="archive-reflection"
        data-reflection-type={reflection.type}
        aria-live="polite"
        style={{
          position: 'fixed',
          top: 28,
          right: 24,
          zIndex: 6,
          maxWidth: 296,
          padding: '14px 18px 14px 16px',
          // Parchment, not white — sits within the warm palette.
          background:
            'linear-gradient(180deg, rgba(248,238,214,0.96) 0%, rgba(242,230,200,0.96) 100%)',
          // Hairline gold at low alpha — discovered, not announced.
          border: '1px solid rgba(168,133,76,0.34)',
          borderRadius: 4,
          opacity,
          transform: phase === 'visible' ? 'translateY(0)' : 'translateY(-6px)',
          transition,
          pointerEvents: 'none',  // discovered, never interacted with
          animation: phase === 'visible'
            ? 'archiveReflectionBreathe 7.2s ease-in-out infinite'
            : 'none',
          // Tiny museum-placard texture: subtle grain via box-shadow inset
          boxShadow: '0 18px 38px rgba(0,0,0,0.42), 0 0 0 1px rgba(168,133,76,0.22)',
        }}
      >
        <div
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontStyle: 'italic',
            fontSize: 13.5,
            lineHeight: 1.45,
            letterSpacing: '0.005em',
            // Deep warm ink — ink on parchment, not text on a card.
            color: 'rgba(58,36,14,0.88)',
            textAlign: 'left',
          }}
        >
          {reflection.message}
        </div>
        {/* Eyebrow microtype — sits BELOW the line, like a museum label */}
        <div
          style={{
            marginTop: 6,
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: 9.5,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'rgba(168,133,76,0.78)',
          }}
        >
          A REFLECTION ✦
        </div>
      </div>
    </>
  );
}
